import {Box, Flex, Skeleton, Text, useMultiStyleConfig, VisuallyHidden} from '@chakra-ui/react'
import PropTypes from 'prop-types'
import React, {Fragment} from 'react'
import {FormattedMessage, FormattedNumber} from 'react-intl'
import {useCurrency} from '../../hooks'
import {useCurrentCustomer} from '../../hooks/use-current-customer'

/**
 * The DisplayPrice is a visual representation of all price settings of products
 * @param {'pdp'|'psdp'|'stickyCta'|'cart'|'productTile'} props.variant - the variant of the product tile:
 * - `pdp`: price display in cockpit of product (price, discount and taxes)
 * - `psdp`: price display in cockpit of productset (price, discount and taxes)
 * - `stickyCta`: price display on mobile for sticky element (price, discount and taxes)
 * - `cart`: display price on cart tile (price, discount, % of saving)
 * - `productTile`: display price on product tile (price and discount)
 *
 */
const DisplayPrice = ({
    price,
    displayPrice,
    currency,
    loading,
    isFree,
    hasDiscount,
    hidePromotionDetails,
    discountPercentage,
    ...props
}) => {
    const {currency: activeCurrency} = useCurrency()
    const {isCustomNetPriceEnabled} = useCurrentCustomer().data
    const showDiscount = hasDiscount && !hidePromotionDetails

    const styles = useMultiStyleConfig('DisplayPrice', {
        ...props,
        isFree,
        hasDiscount: showDiscount,
        isCustomNetPriceEnabled
    })

    // wrap the price in a Skeleton while loading - it sizes to the price text (matching its
    // line-height, so there is no layout shift) and shows the animated placeholder bar
    const SkeletonComponent = loading ? Skeleton : Fragment
    const skeletonProps = loading ? {isLoaded: false, fitContent: true} : {}

    return (
        <Flex alignItems="flex-end" justifyContent="space-between">
            <Box {...styles.container}>
                <Flex {...styles.priceOrder}>
                    {showDiscount && (
                        <SkeletonComponent {...skeletonProps}>
                            <Text {...styles.discount} data-testid="price">
                                <VisuallyHidden>
                                    <FormattedMessage
                                        defaultMessage="Original price: "
                                        id="item_attributes.label.original_price"
                                    />
                                </VisuallyHidden>
                                <FormattedNumber
                                    style="currency"
                                    currency={currency || activeCurrency}
                                    value={price}
                                />
                            </Text>
                        </SkeletonComponent>
                    )}
                    <SkeletonComponent {...skeletonProps}>
                        <Box {...styles.priceContainer}>
                            {showDiscount && (
                                <VisuallyHidden>
                                    <FormattedMessage
                                        defaultMessage="Discounted price: "
                                        id="item_attributes.label.discounted_price"
                                    />
                                </VisuallyHidden>
                            )}
                            <Text
                                {...styles.price}
                                {...(isFree && styles.priceFreeNumber)}
                                data-testid="displayPrice"
                            >
                                <FormattedNumber
                                    style="currency"
                                    currency={currency || activeCurrency}
                                    value={displayPrice}
                                />
                            </Text>
                            {isFree && (
                                <Text
                                    {...styles.price}
                                    {...styles.priceFreeLabel}
                                    data-testid="isFree"
                                >
                                    <FormattedMessage defaultMessage="Free" id="global.free" />
                                </Text>
                            )}
                        </Box>
                    </SkeletonComponent>
                </Flex>
                {showDiscount && (
                    <Box {...styles.discountText} data-testid="discountText">
                        <FormattedMessage
                            defaultMessage="You save {discountPercentage}%"
                            values={{discountPercentage: discountPercentage}}
                            id="item_attributes.label.savings"
                        />
                    </Box>
                )}
                <Text {...styles.taxes} data-testid="taxes">
                    {!isCustomNetPriceEnabled ? (
                        <>
                            <FormattedMessage
                                id="global.taxes_included"
                                defaultMessage="tax included"
                            />
                        </>
                    ) : (
                        <FormattedMessage
                            id="global.taxes_not_included"
                            defaultMessage="tax not included"
                        />
                    )}
                </Text>
            </Box>
        </Flex>
    )
}

DisplayPrice.propTypes = {
    price: PropTypes.number,
    displayPrice: PropTypes.number,
    currency: PropTypes.string,
    loading: PropTypes.bool,
    isFree: PropTypes.bool,
    hasDiscount: PropTypes.bool,
    hidePromotionDetails: PropTypes.bool,
    discountPercentage: PropTypes.number,
    variant: PropTypes.string
}

export default DisplayPrice
